import type { Observable } from 'rxjs';

export interface DatabaseRootDocState {
  readonly loaded: boolean;
  readonly synced: boolean;
  readonly ready: boolean;
  /**
   * Set while the sync keeps this one document apart after a failed read,
   * e.g. the server holds a binding for the database but no stored state.
   */
  readonly syncIsolatedFailure: object | null;
}

export interface DatabaseRootReadinessInput {
  /** Doc engine state of the database root system document. */
  readonly rootState$: Observable<DatabaseRootDocState>;
  /**
   * Resolves once the root's storage block and shard manifest are usable. The
   * storage observer opens the root system document, which connects it to the
   * doc engine; the engine only hydrates connected documents.
   */
  readonly waitForStorage: (signal: AbortSignal) => Promise<void>;
}

/**
 * The root counts as missing once local storage is loaded without data and
 * the remote sync either finished or set the root apart after a failed read.
 * The sync keeps retrying a root it set apart; one that arrives later renders
 * in place.
 */
export function isDatabaseRootMissing(state: DatabaseRootDocState): boolean {
  return (
    state.loaded &&
    !state.ready &&
    (state.synced || state.syncIsolatedFailure !== null)
  );
}

/**
 * Wait until a database host can render, or until its root is known to be
 * missing. A missing database never holds back the page: the page opens and
 * the database block keeps observing its own storage, so a root delivered
 * later renders in place through the regular Y.js update signal.
 */
export async function waitForDatabaseRootReady(
  input: DatabaseRootReadinessInput,
  signal: AbortSignal
): Promise<void> {
  signal.throwIfAborted();
  const storageController = new AbortController();
  const abortStorage = () => storageController.abort(signal.reason);
  signal.addEventListener('abort', abortStorage, { once: true });

  let rootMissing = false;
  const subscription = input.rootState$.subscribe(state => {
    if (!isDatabaseRootMissing(state)) return;
    rootMissing = true;
    storageController.abort();
  });
  try {
    await input.waitForStorage(storageController.signal);
  } catch (error) {
    if (!rootMissing || signal.aborted) throw error;
  } finally {
    subscription.unsubscribe();
    signal.removeEventListener('abort', abortStorage);
  }
}
